import React, { useContext } from 'react';
import { ThemeProvider } from 'emotion-theming';
import omit from 'lodash/omit';
import { genThemedStyled, Theme, ThemeContext } from '../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../color';
import { MEDIA_QUERIES as M } from '../grid';

const GRID_CONSTANTS = {
  columns: 12,
  columnGap: '1rem',
  rowGap: '1rem',
};

export type StyleProps = {
  columnGap?: string;
  rowGap?: string;
  gap?: string;
};

export type ColumnProps = StyleProps & {
  children: React.ReactNode;
};

export type ColumnSize = 'auto' | number | boolean | undefined;
export type ColumnBreakpoints = {
  /**
   * Corresponds to each breakpoint, see breakpoints in @ordermentum/ui-shared/foundations/grid
   * If a number, it sets the number of columns the column item uses
   * It can't be greater than the total number of columns of the grid (12 by default)
   * If 'auto', the column item's width matches its content
   * If true, the column item's width grows to use the space available in the column container
   * If prop is not given the column item uses the space available in the column container
   * The value is applied for the specified breakpoint and wider screens if not overridden
   */
  xs?: ColumnSize; // MOBILE_MAX: 480px
  sm?: ColumnSize; // TABLET_PORTRAIT_AND_SMALLER: 768px
  md?: ColumnSize; // TABLET_LANDSCAPE_AND_SMALLER: 1024px
  lg?: ColumnSize; // SMALL_DESKTOP_AND_SMALLER: 1440px
  xl?: ColumnSize; // Greater than 1440px
};

export type ColumnItemProps = ColumnBreakpoints & {
  children: React.ReactNode;
};

export const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);

  const themeObj = {
    color: colors.SPACE,
  };
  return themeObj;
};

type themeStyles = {
  color: ColorCodes;
};

export const ThemeStyled = genThemedStyled<themeStyles>();

const getGridGaps = ({ columnGap, rowGap, gap }: StyleProps) => {
  const columnSpace = gap || columnGap || GRID_CONSTANTS.columnGap;
  const rowSpace = gap || rowGap || GRID_CONSTANTS.rowGap;
  return {
    margin: `calc(${rowSpace} / -2) calc(${columnSpace} / -2)`,
    '> #column-item': {
      padding: `calc(${rowSpace} / 2) calc(${columnSpace} / 2)`,
    },
  };
};

const ColumnWrapper = ThemeStyled.div<{ styles: StyleProps }>(({ styles }) => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: 0,
  boxSizing: 'border-box',
  flexWrap: 'wrap',
  ...getGridGaps(styles),
}));

const getColumnItemSizes = (column: ColumnSize) => {
  if (typeof column === 'number') {
    return {
      flexGrow: 0,
      flexBasis: 'auto',
      width: `calc(100% * ${column} / ${GRID_CONSTANTS.columns})`,
    };
  }
  if (column === 'auto') {
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
      width: 'auto',
      maxWidth: 'none',
    };
  }
  return {
    flexGrow: 1,
    flexBasis: 0,
    maxWidth: '100%',
  };
};

const ColumnItemWrapper = ThemeStyled.div<{
  columns: ColumnBreakpoints;
  theme: themeStyles;
}>(({ columns, theme }) => ({
  minWidth: 0,
  boxSizing: 'border-box',
  ...getColumnItemSizes(
    columns.xl || columns.lg || columns.md || columns.sm || columns.xs
  ),
  color: theme.color,
  [M.SMALL_DESKTOP_AND_SMALLER]: {
    ...getColumnItemSizes(columns.lg || columns.md || columns.sm || columns.xs),
  },
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    ...getColumnItemSizes(columns.md || columns.sm || columns.xs),
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    ...getColumnItemSizes(columns.sm || columns.xs),
  },
  [M.MOBILE_ONLY]: {
    ...getColumnItemSizes(columns.xs),
  },
}));

/**
 * Column
 *
 * @argument columnGap string - for gaps between columns. accepts a string or the futuro sizing system
 * @argument rowGap string - for gaps between rows. accepts a string or the futuro sizing system
 * @argument gap string - shorthand for both column and row gaps. accepts a string or the futuro sizing system
 * @argument children react node - accepts children and wraps the column container around it
 *
 *
 */

export const Column = (props: ColumnProps) => {
  const styles = { ...omit(props, 'children') };

  return <ColumnWrapper styles={styles}>{props.children}</ColumnWrapper>;
};

/**
 * ColumnItem
 *
 * For more details about the props, please see ColumnBreakpoints above
 * @argument xs 'auto' | number | boolean - value is applied for the `MOBILE_MAX` breakpoint and wider screens if not overridden
 * @argument sm 'auto' | number | boolean - value is applied for the `TABLET_PORTRAIT_AND_SMALLER smalle` breakpoint and wider screens if not overridden
 * @argument md 'auto' | number | boolean - value is applied for the `TABLET_LANDSCAPE_AND_SMALLER` breakpoint and wider screens if not overridden
 * @argument lg 'auto' | number | boolean - value is applied for the `SMALL_DESKTOP_AND_SMALLER` breakpoint and wider screens if not overridden
 * @argument xl 'auto' | number | boolean - value is applied for `Screens greater than 1440px` breakpoint and wider screens if not overridden
 * @argument children react node - accepts children and wraps the column item around it
 *
 */

export const ColumnItem = (props: ColumnItemProps) => {
  const columns = { ...omit(props, 'children') };

  return (
    <ThemeProvider theme={getTheme(useContext(ThemeContext))}>
      <ColumnItemWrapper id="column-item" columns={columns}>
        {props.children}
      </ColumnItemWrapper>
    </ThemeProvider>
  );
};
